import {Component, signal, inject, DestroyRef} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {StoreClosureService} from '../../../../services/http/store/store-closure.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/**
 * Data required to open the store closure delete modal.
 */
export interface StoreClosureDeleteData {
  storeId?: string;
  date: string;
}

/**
 * This component handles deleting a store closure.
 */
@Component({
  selector: 'app-store-closure-delete',
  templateUrl: './store-closure-delete.component.html',
  styleUrls: ['./store-closure-delete.component.less'],
  imports: [SHARED_IMPORTS]
})
export class StoreClosureDeleteComponent {
  private storeClosureService = inject(StoreClosureService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** An identifier of a store. */
  readonly storeId = signal<string | undefined>(undefined);
  /** The date on which the store closure should be deleted. */
  readonly date = signal<string | undefined>(undefined);

  /** `True` if request to backend is in progress. */
  loading = signal(false);

  static open(modalService: NgbModal, data: StoreClosureDeleteData): NgbModalRef {
    const modalRef = modalService.open(StoreClosureDeleteComponent);
    const instance = modalRef.componentInstance as StoreClosureDeleteComponent;
    instance.storeId.set(data.storeId);
    instance.date.set(data.date);
    return modalRef;
  }

  /**
   * Sends request to backend to delete a store closure.
   * @param modalComponent a modal component of a store closure form.
   */
  confirm(modalComponent: ModalComponent): void {
    this.loading.set(true);

    this.storeClosureService.deleteStoreClosure(this.storeId(), this.date())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Store closed has been successfully deleted');
                this.ruleCheckerService.check();
              },
              error: error => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}